import { Button } from "@/components/ui/button";
import { useHashNavigation } from "@/hooks/useHashNavigation";
import { cn } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Database, Eye, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_public/privacy")({
  component: PrivacyComponent,
});

function PrivacyComponent() {
  const { t } = useTranslation();
  const { navTo, activeHash } = useHashNavigation("/privacy");

  const sections = [
    "collection",
    "usage",
    "storage-security",
    "artisan-privacy",
    "cookies",
    "your-rights",
  ];

  return (
    <div className="min-h-screen py-12 md:py-20">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navTo()}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            <span>{t("legal.shared.backToHome")}</span>
          </Button>
        </div>

        <div className="border-b pb-8 mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-wider mb-2">
              <ShieldCheck className="h-4 w-4" />
              <span>{t("legal.privacy.label")}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("legal.privacy.title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {t("legal.shared.lastUpdated")}: {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-medium text-muted-foreground">
            <Eye className="h-3.5 w-3.5 text-primary" />
            <span>{t("legal.privacy.badge")}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <aside className="hidden md:block md:col-span-1 sticky top-24 h-fit">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
              {t("legal.shared.navigation")}
            </p>
            <nav className="flex flex-col gap-2 border-s">
              {sections.map((sec) => (
                <button
                  key={sec}
                  onClick={() => navTo(sec)}
                  className={cn(
                    "text-xs text-start border-s-2 border-transparent -ms-px ps-4 py-1 transition-colors cursor-pointer",
                    activeHash === sec
                      ? "text-primary border-primary font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t(`legal.privacy.sections.${sec}.nav`)}
                </button>
              ))}
            </nav>
          </aside>

          <main className="col-span-1 md:col-span-3 space-y-10 text-sm text-muted-foreground">
            {sections.map((sec) => (
              <section key={sec} id={sec} className="scroll-mt-24 space-y-3">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <span className="text-xs font-mono text-primary/50">//</span>
                  {t(`legal.privacy.sections.${sec}.heading`)}
                </h2>
                <p className="whitespace-pre-line leading-relaxed">
                  {t(`legal.privacy.sections.${sec}.content`)}
                </p>
              </section>
            ))}

            <div className="p-5 rounded-xl border bg-muted/40 flex items-start gap-4 text-xs">
              <Database className="h-5 w-5 shrink-0 text-primary mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-semibold text-foreground">
                  {t("legal.privacy.storageNotice.title")}
                </h4>
                <p className="leading-normal">{t("legal.privacy.storageNotice.description")}</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
